import { clsx } from 'clsx';
import React, { CSSProperties, Ref, useCallback, useRef } from 'react';
import { Button as AriaButton, PressEvent, Link as AriaLink } from 'react-aria-components';
import CircleSpinner from '~/components/data-display/CircleSpinner/CircleSpinner';
import styles from './IconButton.module.scss';

type IconButtonSize = 'extrasmall' | 'small' | 'medium' | 'large';
type IconButtonVariant = 'outlined' | 'ghost';

interface LinkProps {
  href: string;
  target?: '_blank';
  type?: never;
}

interface ButtonProps {
  type?: 'button' | 'submit';
  href?: never;
  target?: never;
}

type ButtonIconProps = {
  action?: 'confirm' | 'destructive';
  className?: string;
  'data-testid'?: string;
  'data-intercom-target'?: string;
  disabled?: boolean;
  Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  iconClassName?: string;
  label?: string;
  isPending?: boolean;
  onClick?: (e: PressEvent) => void;
  onPressStart?: (e: PressEvent) => void;
  size?: IconButtonSize;
  slot?: string;
  style?: CSSProperties;
  variant?: IconButtonVariant;
  ref?: Ref<HTMLElement>;
} & (LinkProps | ButtonProps);

const IconButton: React.FC<ButtonIconProps> = ({
  action,
  className,
  'data-testid': testId,
  'data-intercom-target': intercomTarget,
  disabled,
  Icon,
  iconClassName,
  isPending,
  label,
  onClick,
  onPressStart,
  size = 'medium',
  slot,
  style,
  variant,
  href,
  type = 'button',
  target,
  ref,
}) => {
  const computedClassName = clsx(
    styles.button,
    { [styles.destructive!]: action === 'destructive', [styles.confirm!]: action === 'confirm' },
    styles[size],
    variant ? styles[variant] : null,
    className,
  );

  const iconStyle = clsx(styles.icon, iconClassName);
  const myRef = useRef<HTMLElement | null>(null);

  const setRef = useCallback(
    (element: HTMLElement | null) => {
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
      myRef.current = element;
    },
    [ref],
  );

  if (href) {
    return (
      <AriaLink
        className={computedClassName}
        href={href}
        target={target}
        isDisabled={disabled}
        onPress={onClick}
        onPressStart={onPressStart}
        ref={setRef}
        data-intercom-target={intercomTarget}
      >
        {isPending ? <CircleSpinner className={iconStyle} /> : <Icon className={iconStyle} />}
      </AriaLink>
    );
  }

  return (
    <AriaButton
      aria-label={label}
      className={computedClassName}
      data-testid={testId}
      data-intercom-target={intercomTarget}
      isDisabled={disabled}
      isPending={isPending}
      ref={setRef}
      onPress={onClick}
      onPressStart={onPressStart}
      style={style}
      type={type}
      slot={slot}
    >
      {isPending ? <CircleSpinner className={iconStyle} /> : <Icon className={iconStyle} />}
    </AriaButton>
  );
};

export default IconButton;
